import { FailureEventArgs } from "@syncfusion/ej2-react-grids";
import { DataManager } from "@syncfusion/ej2-data";
import GridColumn from "../../components/Grids/GridColumns/GridColumn";
import GridODataV4Adaptor from "../../components/Grids/GridOData/GridODataV4Adaptor";
import AppToasterV2 from "../../components/ToastV2/ToastV2";
import { GridActionFailureError } from "../../components/Grids/GridOData/GridActionFailureError";
import { useEffect, useState } from "react";
import { FieldSettingsModel } from "@syncfusion/ej2-react-dropdowns";
import AsyncAutocompleteODataV4Adaptor from "../../components/Fields/DropdownFields/AsyncAutoCompleteField/AsyncAutoCompleteODataV4Adaptor";
import AsyncMultiSelectODataV4Adaptor from "../../components/Fields/DropdownFields/AsyncMultiSelectField/AsyncMultiSelectODataV4Adaptor";
import {
  ODataV4BeforeSendArgs,
  ODataV4QueryStringAndMaybeHeadersFactory,
  ODataV4TransformDataMethod,
} from "./types";
import BaseODataV4Adaptor, {
  BaseODataV4AdaptorOptions,
} from "./BaseODataV4Adaptor";

export interface ODataProps {
  getAuthRequestHeader: () => Promise<Record<string, string> | undefined>;
}

export const useOData = (props: ODataProps, init?: () => void) => {
  const [getAuthRequestHeader, setGetAuthRequestHeader] = useState<
    () => Promise<Record<string, string> | undefined>
  >(props.getAuthRequestHeader);
  const [authRequestHeader, setAuthRequestHeader] = useState<
    Record<string, string> | undefined
  >();

  const [odataReady, setOdataReady] = useState(false);

  useEffect(() => {
    initRequestHeader();
  }, []);

  useEffect(() => {
    if (authRequestHeader) {
      setOdataReady(true);
      init?.();
    } else {
      setOdataReady(false);
    }

    return () => setOdataReady(false);
  }, [authRequestHeader]);

  let failureTimeoutCount: number = 0;

  const initRequestHeader = async () => {
    setGetAuthRequestHeader(getAuthRequestHeader);

    const header = await props.getAuthRequestHeader();
    setAuthRequestHeader(header);
  };

  const headers = !!authRequestHeader
    ? Object.entries(authRequestHeader).map(([key, value]) => ({
        [key]: value,
      }))
    : undefined;

  const setupGridODataSource = (
    odataUrl: string,
    columnsConfiguration?: GridColumn[],
    beforeSendFn?: (
      beforeSendArgs: ODataV4BeforeSendArgs
    ) => [string, Record<string, string> | undefined]
  ): DataManager => {
    const dataManager = new DataManager({
      url: odataUrl,
      adaptor: new GridODataV4Adaptor(
        columnsConfiguration,
        beforeSendFn ? beforeSendFn : baseOnBeforeSend
      ),
      crossDomain: true,
    });

    return dataManager;
  };

  // Setup
  const setupAsyncAutoCompleteODataSource = (
    odataUrl: string,
    fieldSettings?: FieldSettingsModel,
    hasNotInListOption?: boolean,
    groupByActiveStatus?: boolean
  ): DataManager => {
    const dataManager = new DataManager({
      url: odataUrl,
      adaptor: new AsyncAutocompleteODataV4Adaptor(
        !!hasNotInListOption,
        !!groupByActiveStatus,
        fieldSettings,
        baseOnBeforeSend
      ),
      crossDomain: true,
      headers,
    });
    return dataManager;
  };

  const setupODataManagerWithAdaptor = <TData = any>(
    odataUrl: string,
    adaptor: BaseODataV4Adaptor<TData>
  ): DataManager => {
    return new DataManager({
      url: odataUrl,
      crossDomain: true,
      adaptor,
      headers, // does not get passed to the adaptor.
    });
  };

  const setupBaseODataManager = <TData = any>(
    odataUrl: string,
    options: BaseODataV4AdaptorOptions<TData>
  ): DataManager =>
    setupODataManagerWithAdaptor(
      odataUrl,
      new BaseODataV4Adaptor<TData>({
        ...options,
        beforeSendFn: options.beforeSendFn
          ? options.beforeSendFn
          : baseOnBeforeSend,
      })
    );

  const setupAsyncODataDropdownDataManager = <TData = any>(
    odataUrl: string,
    transformData?: ODataV4TransformDataMethod<TData>,
    removeLeadingQuestionMark: boolean = true,
    replaceUndefinedInQueryStringWithNulls: boolean = true,
    removeUnsupportedParameters: boolean = true
  ): DataManager =>
    setupBaseODataManager<TData>(odataUrl, {
      beforeSendFn: baseOnBeforeSend,
      transformData,
      removeLeadingQuestionMark,
      replaceUndefinedInQueryStringWithNulls,
      removeUnsupportedParameters,
    });

  const setupAsyncMultiselectODataSource = (
    odataUrl: string,
    groupByActiveStatus?: boolean,
    transformData?: ODataV4TransformDataMethod
  ): DataManager => {
    const dataManager = new DataManager({
      url: odataUrl,
      adaptor: new AsyncMultiSelectODataV4Adaptor(
        !!groupByActiveStatus,
        baseOnBeforeSend,
        transformData
      ),
      crossDomain: true,
      headers,
    });
    return dataManager;
  };

  const setFailureTimeoutCount = (value: number) => {
    failureTimeoutCount = value;
  };

  const baseActionCompleteEventHandler = () => {
    setFailureTimeoutCount(0);
  };

  const baseActionFailureEventHandler = async (
    eventArgs: FailureEventArgs,
    refreshFn: () => void
  ) => {
    // Process Error
    const errorStatus =
      (eventArgs.error as unknown as XMLHttpRequest)?.status ||
      (eventArgs.error as unknown as GridActionFailureError)?.error.status ||
      0;
    if (!!eventArgs.error && [401, 403].includes(errorStatus)) {
      setFailureTimeoutCount(failureTimeoutCount + 1);

      //eslint-disable-next-line @typescript-eslint/no-unused-expressions
      failureTimeoutCount;

      if (failureTimeoutCount < 3) {
        //if (props.getAuthRequestHeader) {
        if (getAuthRequestHeader) {
          //const token = await props.getAuthRequestHeader();
          const token = await getAuthRequestHeader();
          setAuthRequestHeader(token);
        }

        refreshFn();
      } else {
        AppToasterV2.showFailedActionToaster(
          "Authorization Error. Please refresh and try again."
        );
      }
    } else {
      AppToasterV2.showFailedActionToaster(
        "An error occurred executing the current action."
      );
    }
  };

  // Base Methods
  const baseOnBeforeSend: ODataV4QueryStringAndMaybeHeadersFactory = ({
    queryString,
  }: ODataV4BeforeSendArgs) => {
    return [queryString, authRequestHeader];
  };

  const baseActionCompleteEvent = () => {
    return () => {
      baseActionCompleteEventHandler();
    };
  };

  const baseActionFailureEvent = (refreshFn: () => void) => {
    return async (eventArgs: FailureEventArgs) => {
      // Process Error
      await baseActionFailureEventHandler(eventArgs, refreshFn);
    };
  };

  return {
    init,
    authRequestHeader,
    setupGridODataSource,
    setupAsyncAutoCompleteODataSource,
    setupAsyncMultiselectODataSource,
    setupAsyncODataDropdownDataManager,
    setupBaseODataManager,
    setupODataManagerWithAdaptor,
    baseActionCompleteEvent,
    baseActionFailureEvent,
    baseActionCompleteEventHandler,
    baseActionFailureEventHandler,
    odataReady,
  };
};
